// Lightweight typed fetch wrapper for the admin REST API.
//
// In dev: Vite proxies /api → http://localhost:8000
// In prod: served same-origin behind Caddy.

import { useAuthStore } from "./auth";
import { useCapModal } from "./capModal";
import { maybeRefreshSession } from "./session";

const BASE = "/api/admin";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string = "api_error",
    public details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type QueryScalar = string | number | boolean;
type QueryValue = QueryScalar | QueryScalar[] | undefined | null;

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, QueryValue>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = options;

  // Sliding session: renew the JWT when it's near expiry so active
  // operators never hit the hard 401 mid-work (lib/session.ts).
  await maybeRefreshSession();

  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    // Arrays are emitted as repeated keys (?tag=a&tag=b), which FastAPI
    // collects into a list[str] query parameter. Skip null/undefined and
    // empty arrays so the URL stays clean.
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) {
        v.forEach((item) => {
          if (item !== undefined && item !== null) {
            url.searchParams.append(k, String(item));
          }
        });
      } else {
        url.searchParams.set(k, String(v));
      }
    });
  }

  const token = useAuthStore.getState().token;
  const init: RequestInit = {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  const response = await fetch(url, init);
  return parseResponse<T>(response);
}

/** Shared response handling: error mapping (+ logout on 401), 204 → void,
 *  otherwise parse JSON. Used by both `request` and `upload`. */
async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let payload: { error?: { code: string; message: string; details?: Record<string, unknown> } } = {};
    try {
      payload = await response.json();
    } catch {
      // not JSON
    }
    const err = payload.error;
    if (response.status === 401) {
      useAuthStore.getState().logout();
    }
    // A plan QUOTA breach (ADR-0041 `plan_limit_reached`, e.g. the 26th workflow) → open
    // the global cap modal: it's an action-time event, not a page the user navigated to.
    // FEATURE locks (`plan_feature_locked`) are NOT modal'd here — the FeatureGate renders
    // a frosted lock panel on the gated page instead (a dismissable modal that let you keep
    // using the page was useless), so we suppress the modal for those.
    if (response.status === 402 && err?.code === "plan_limit_reached") {
      const d = (err.details ?? {}) as { metric?: string; limit?: number | null };
      useCapModal.getState().open({
        code: err.code,
        message: err.message,
        metric: d.metric,
        limit: d.limit,
      });
    }
    throw new ApiError(
      response.status,
      err?.message ?? response.statusText,
      err?.code ?? "api_error",
      err?.details ?? {},
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/** Multipart POST: send a FormData body WITHOUT a Content-Type header so
 *  the browser sets the multipart boundary itself. Used for file uploads
 *  (form attachments). */
async function upload<T>(path: string, formData: FormData): Promise<T> {
  await maybeRefreshSession();
  const url = new URL(BASE + path, window.location.origin);
  const token = useAuthStore.getState().token;
  const response = await fetch(url, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  return parseResponse<T>(response);
}

export const api = {
  get:  <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "POST", body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "DELETE" }),
  upload,
};
